import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { useAuth } from './AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FavoritesContext = createContext(null);

/**
 * FavoritesProvider - Centralized favorites management to avoid N+1 API calls
 * 
 * Strategy:
 * 1. On login, load all user favorites in one API call
 * 2. Components read from local cache (instant)
 * 3. Add/remove updates both cache and backend
 * 4. For new sessions not in initial load, batch check with debounce
 */
export function FavoritesProvider({ children }) {
  const { isAuthenticated, user } = useAuth();
  const [favorites, setFavorites] = useState(new Set());
  const [pendingChecks, setPendingChecks] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const checkedSessions = useRef(new Set()); // Track sessions we've already checked

  // Load all favorites when user logs in
  const loadAllFavorites = useCallback(async () => {
    if (!isAuthenticated) return;
    
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/favorites`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        const favoriteSessionIds = new Set(data.map(f => f.session_id));
        setFavorites(favoriteSessionIds);
        // Mark all loaded favorites as checked
        data.forEach(f => checkedSessions.current.add(f.session_id));
      }
    } catch (error) {
      console.error('Error loading favorites:', error);
    } finally {
      setLoading(false);
      setInitialized(true);
    }
  }, [isAuthenticated]);

  // Batch check favorites for sessions not in initial load
  const batchCheckFavorites = useCallback(async (sessionIds) => {
    if (!isAuthenticated || sessionIds.length === 0) return;

    try {
      const response = await fetch(`${API_URL}/api/favorites/check-batch`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ session_ids: sessionIds })
      });

      if (response.ok) {
        const data = await response.json();
        
        setFavorites(prev => {
          const newFavorites = new Set(prev);
          Object.entries(data.favorites).forEach(([sessionId, isFav]) => {
            if (isFav) {
              newFavorites.add(sessionId);
            } else {
              newFavorites.delete(sessionId);
            }
            // Mark as checked
            checkedSessions.current.add(sessionId);
          });
          return newFavorites;
        });
      }
    } catch (error) {
      console.error('Error batch checking favorites:', error);
    }
  }, [isAuthenticated]);

  // Process pending checks with debounce
  useEffect(() => {
    if (pendingChecks.size === 0 || !isAuthenticated || !initialized) return;

    const timer = setTimeout(() => {
      const sessionIds = Array.from(pendingChecks);
      setPendingChecks(new Set());
      batchCheckFavorites(sessionIds);
    }, 100); // 100ms debounce

    return () => clearTimeout(timer);
  }, [pendingChecks, isAuthenticated, initialized, batchCheckFavorites]);

  // Queue a session ID to be checked (only if not already checked)
  const queueCheck = useCallback((sessionId) => {
    if (!isAuthenticated || !initialized) return;
    if (checkedSessions.current.has(sessionId)) return; // Already checked
    
    setPendingChecks(prev => {
      const newSet = new Set(prev);
      newSet.add(sessionId);
      return newSet;
    });
  }, [isAuthenticated, initialized]);

  // Check if a session is favorited (synchronous, from cache)
  const isFavorite = useCallback((sessionId) => {
    return favorites.has(sessionId);
  }, [favorites]);

  // Add to favorites
  const addFavorite = useCallback(async (sessionId) => {
    if (!isAuthenticated) return false;

    // Optimistic update
    setFavorites(prev => new Set([...prev, sessionId]));

    try {
      const response = await fetch(`${API_URL}/api/favorites`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ session_id: sessionId })
      });

      if (response.ok) {
        checkedSessions.current.add(sessionId);
        return true;
      } else {
        // Revert on failure
        setFavorites(prev => {
          const newSet = new Set(prev);
          newSet.delete(sessionId);
          return newSet;
        });
      }
    } catch (error) {
      console.error('Error adding favorite:', error);
      // Revert on error
      setFavorites(prev => {
        const newSet = new Set(prev);
        newSet.delete(sessionId);
        return newSet;
      });
    }
    return false;
  }, [isAuthenticated]);

  // Remove from favorites
  const removeFavorite = useCallback(async (sessionId) => {
    if (!isAuthenticated) return false;

    // Optimistic update
    setFavorites(prev => {
      const newSet = new Set(prev);
      newSet.delete(sessionId);
      return newSet;
    });

    try {
      const response = await fetch(`${API_URL}/api/favorites/${sessionId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        return true;
      } else {
        // Revert on failure
        setFavorites(prev => new Set([...prev, sessionId]));
      }
    } catch (error) {
      console.error('Error removing favorite:', error);
      // Revert on error
      setFavorites(prev => new Set([...prev, sessionId]));
    }
    return false;
  }, [isAuthenticated]);

  // Toggle favorite
  const toggleFavorite = useCallback(async (sessionId) => {
    if (favorites.has(sessionId)) {
      return await removeFavorite(sessionId);
    } else {
      return await addFavorite(sessionId);
    }
  }, [favorites, addFavorite, removeFavorite]);

  // Load all favorites on auth change
  useEffect(() => {
    if (isAuthenticated && !initialized) {
      loadAllFavorites();
    } else if (!isAuthenticated) {
      setFavorites(new Set());
      setInitialized(false);
      checkedSessions.current = new Set();
    }
  }, [isAuthenticated, initialized, loadAllFavorites]);

  const value = {
    favorites,
    isFavorite,
    queueCheck,
    addFavorite,
    removeFavorite,
    toggleFavorite,
    loading,
    initialized
  };

  return (
    <FavoritesContext.Provider value={value}>
      {children}
    </FavoritesContext.Provider>
  );
}

export function useFavorites() {
  const context = useContext(FavoritesContext);
  if (!context) {
    throw new Error('useFavorites must be used within a FavoritesProvider');
  }
  return context;
}

export default FavoritesContext;
